import { useState } from 'react'
import { Link, useNavigate } from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { registerSchema, type RegisterInput } from '@ats/shared'
import { authApi } from '@/lib/api'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { toast } from '@/components/ui/toaster'

export function RegisterPage() {
  const navigate = useNavigate()
  const { setAuth } = useAuthStore()
  const [isLoading, setIsLoading] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<RegisterInput>({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      tenantSlug: '',
    },
  })

  const tenantName = watch('tenantName')

  // Auto-generate slug from company name
  const suggestedSlug = tenantName
    ?.toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')

  const onSubmit = async (data: RegisterInput) => {
    setIsLoading(true)
    try {
      const response = await authApi.register(data)
      setAuth(
        response.user as any,
        response.tenant as any,
        response.accessToken,
        response.refreshToken
      )
      toast({ title: 'Account created!', description: 'Welcome to ATS', variant: 'success' })
      navigate({ to: '/' })
    } catch (err) {
      toast({
        title: 'Registration failed',
        description: err instanceof Error ? err.message : 'Something went wrong',
        variant: 'destructive',
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-bold">Create your account</h2>
        <p className="text-muted-foreground mt-1">
          Start hiring smarter with ATS
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="tenantName">Company name</Label>
          <Input
            id="tenantName"
            placeholder="Acme Inc"
            error={!!errors.tenantName}
            {...register('tenantName')}
          />
          {errors.tenantName && (
            <p className="text-sm text-destructive">{errors.tenantName.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="tenantSlug">Company URL</Label>
          <div className="flex">
            <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-input bg-muted text-sm text-muted-foreground">
              ats.app/
            </span>
            <Input
              id="tenantSlug"
              className="rounded-l-none"
              placeholder={suggestedSlug || 'acme'}
              error={!!errors.tenantSlug}
              {...register('tenantSlug')}
            />
          </div>
          {errors.tenantSlug && (
            <p className="text-sm text-destructive">{errors.tenantSlug.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Your name</Label>
          <Input
            id="name"
            placeholder="John Smith"
            error={!!errors.name}
            {...register('name')}
          />
          {errors.name && (
            <p className="text-sm text-destructive">{errors.name.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="email">Work email</Label>
          <Input
            id="email"
            type="email"
            placeholder="john@acme.com"
            error={!!errors.email}
            {...register('email')}
          />
          {errors.email && (
            <p className="text-sm text-destructive">{errors.email.message}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <Input
            id="password"
            type="password"
            placeholder="At least 8 characters"
            error={!!errors.password}
            {...register('password')}
          />
          {errors.password && (
            <p className="text-sm text-destructive">{errors.password.message}</p>
          )}
        </div>

        <Button type="submit" className="w-full" loading={isLoading}>
          Create account
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        Already have an account?{' '}
        <Link to="/login" className="text-foreground font-medium hover:underline">
          Sign in
        </Link>
      </p>
    </div>
  )
}
